import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NbButton } from "../components/nb";
import { Heart, BadgeCheck, MapPin, Briefcase, Lock, QrCode } from "lucide-react";

export default function PublicProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/profile/public/${slug}`); setP(data); }
      catch { setErr(true); }
    })();
  }, [slug]);

  if (err) return (
    <div className="min-h-screen grain flex items-center justify-center p-4">
      <div className="bg-white nb-border-thick nb-shadow-lg p-10 text-center max-w-md">
        <QrCode className="w-12 h-12 mx-auto mb-4" />
        <h1 className="font-head font-black text-2xl mb-2">Profile not found</h1>
        <p className="text-gray-600 font-medium mb-6">This QR code doesn't point to an active profile.</p>
        <NbButton as={Link} to="/">Go Home</NbButton>
      </div>
    </div>
  );

  if (!p) return <div className="min-h-screen grain flex items-center justify-center font-head font-bold uppercase">Loading...</div>;

  const photos = p.photos?.length ? p.photos : (p.avatar ? [p.avatar] : [`https://api.dicebear.com/7.x/thumbs/svg?seed=${p.id}`]);

  return (
    <div className="min-h-screen grain">
      <div className="bg-white border-b border-stone-100 py-4">
        <Link to="/" className="flex items-center justify-center">
          <img src="/logo.png" alt="qrcupid" className="h-12 w-auto" />
        </Link>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="bg-[#8B1E3F] nb-border-thick nb-shadow-lg p-2 mb-6">
          <div className="aspect-square nb-border overflow-hidden bg-white">
            <img src={photos[0]} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="bg-white nb-border nb-shadow p-6 text-center">
          <h1 className="font-head font-black text-3xl tracking-tight flex items-center justify-center gap-2">
            {p.name}{p.private ? "" : (p.age ? `, ${p.age}` : "")}
            {p.verified_badge && <BadgeCheck className="w-6 h-6 text-[#D4AF37]" fill="#0A0A0A" />}
          </h1>

          {p.private ? (
            <div className="mt-6 bg-[#F3F3F1] nb-border p-6">
              <Lock className="w-8 h-8 mx-auto mb-2" />
              <p className="font-medium">This profile is private. Sign in to connect.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap justify-center gap-3 mt-3 text-sm font-medium text-gray-700">
                {p.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{p.location}</span>}
                {p.occupation && <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" />{p.occupation}</span>}
              </div>
              <p className="mt-4 font-medium text-gray-800">{p.bio || "No bio yet."}</p>
              {p.interests?.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {p.interests.map((i) => <span key={i} className="bg-[#F3E8E6] nb-border px-2 py-1 text-xs font-head font-bold uppercase">{i}</span>)}
                </div>
              )}
            </>
          )}

          <div className="mt-6">
            {user ? (
              <NbButton as={Link} to={`/members/${p.id}`} className="w-full" data-testid="public-connect"><Heart className="w-4 h-4" fill="white" /> Connect on qrcupid</NbButton>
            ) : (
              <div className="space-y-2">
                <NbButton as={Link} to="/register" className="w-full" data-testid="public-join">Join qrcupid to connect</NbButton>
                <NbButton as={Link} to="/login" variant="outline" className="w-full">Log In</NbButton>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-gray-500 mt-6 font-medium">Scanned at an event? This is {p.name}'s qrcupid profile.</p>
      </div>
    </div>
  );
}
